import { StyleSheet, View, type ViewStyle } from "react-native";
import { SafeAreaView, type Edge } from "react-native-safe-area-context";

import { KeyboardScrollView } from "@/components/ui/keyboard-view";
import { MaxContentWidth, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";

export type ScreenProps = {
  children: React.ReactNode;
  scroll?: boolean;
  padded?: boolean;
  edges?: readonly Edge[];
  contentStyle?: ViewStyle;
  footer?: React.ReactNode;
};

export function Screen({
  children,
  scroll = false,
  padded = true,
  edges = ["top", "bottom"],
  contentStyle,
  footer,
}: ScreenProps) {
  const c = useTheme();

  const inner = (
    <View style={[styles.inner, padded && styles.padded, contentStyle]}>
      {children}
    </View>
  );

  return (
    <SafeAreaView
      style={[styles.safe, { backgroundColor: c.background }]}
      edges={edges}
    >
      {scroll ? (
        // Keyboard-aware, never a bare ScrollView: screens built on Screen are
        // forms, and a plain ScrollView leaves the focused field under the
        // keyboard. Do NOT nest a KeyboardAvoidingView inside this — a
        // padding-behaviour KAV inside a scroll view grows the content by the
        // keyboard height, which moves the KAV, which grows the content again.
        <KeyboardScrollView
          style={styles.flex}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {inner}
        </KeyboardScrollView>
      ) : (
        <View style={styles.flex}>{inner}</View>
      )}
      {footer ? (
        // Pinned to the bottom of the screen and left there — the keyboard is
        // allowed to cover it. Lifting it above the keyboard instead squeezes
        // the form into the strip that remains and puts the button on top of
        // the field being typed into. The scroll view above keeps the focused
        // input clear of the keyboard, so the footer is reachable again the
        // moment the keyboard is dismissed.
        <View style={[styles.footer, padded && styles.padded]}>{footer}</View>
      ) : null}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  flex: { flex: 1 },
  inner: {
    flex: 1,
    width: "100%",
    maxWidth: MaxContentWidth,
    alignSelf: "center",
  },
  padded: { paddingHorizontal: Spacing.xl },
  scrollContent: { flexGrow: 1, paddingVertical: Spacing.lg },
  footer: {
    width: "100%",
    maxWidth: MaxContentWidth,
    alignSelf: "center",
    paddingVertical: Spacing.lg,
  },
});
